import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../App';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

export default function SourcePDFViewer({ documentId, initialPage = 1, onClose }) {
  const canvasRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jumpInput, setJumpInput] = useState('');
  const renderTaskRef = useRef(null);

  useEffect(() => {
    loadPdf();
  }, [documentId]);

  useEffect(() => {
    if (pdfDoc) renderPage(currentPage);
  }, [pdfDoc, currentPage]);

  const loadPdf = async () => {
    setLoading(true);
    setError(null);
    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

      const token = localStorage.getItem('cmbs_token');
      const response = await fetch(`${API_BASE}/diligence/document/${documentId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to load PDF');

      const arrayBuffer = await response.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setCurrentPage(Math.min(initialPage, pdf.numPages));
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const renderPage = async (pageNum) => {
    if (!pdfDoc || !canvasRef.current) return;

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
    }

    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1.4 });
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderTask = page.render({ canvasContext: ctx, viewport });
      renderTaskRef.current = renderTask;
      await renderTask.promise;
    } catch (err) {
      if (err.name !== 'RenderingCancelledException') {
        console.error('Render error:', err);
      }
    }
  };

  const goTo = (page) => {
    const p = Math.max(1, Math.min(totalPages, page));
    setCurrentPage(p);
  };

  const handleJump = (e) => {
    e.preventDefault();
    const p = parseInt(jumpInput, 10);
    if (!isNaN(p)) goTo(p);
    setJumpInput('');
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      {/* Toolbar */}
      <div style={{
        width: '100%', background: COLORS.navy, padding: '10px 20px',
        display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
      }}>
        <span style={{ color: COLORS.gold, fontFamily: 'Georgia, serif', fontSize: 14, fontWeight: 600, flex: 1 }}>
          PDF Viewer
        </span>

        {totalPages > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => goTo(currentPage - 1)} disabled={currentPage <= 1}
              style={{ padding: '4px 12px', background: '#1A2A42', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14, opacity: currentPage <= 1 ? 0.4 : 1 }}>
              ←
            </button>
            <span style={{ color: '#fff', fontSize: 12 }}>
              Page {currentPage} of {totalPages}
            </span>
            <button onClick={() => goTo(currentPage + 1)} disabled={currentPage >= totalPages}
              style={{ padding: '4px 12px', background: '#1A2A42', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 14, opacity: currentPage >= totalPages ? 0.4 : 1 }}>
              →
            </button>
            <form onSubmit={handleJump} style={{ display: 'flex', gap: 4 }}>
              <input
                value={jumpInput}
                onChange={e => setJumpInput(e.target.value)}
                placeholder="Go to page"
                style={{ width: 80, padding: '4px 8px', background: '#1A2A42', color: '#fff', border: `1px solid #2A3A52`, borderRadius: 4, fontSize: 11 }}
              />
              <button type="submit" style={{ padding: '4px 10px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                Go
              </button>
            </form>
          </div>
        )}

        <button onClick={onClose}
          style={{ padding: '5px 14px', background: 'transparent', border: `1px solid ${COLORS.gold}`, color: COLORS.gold, borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
          Close
        </button>
      </div>

      {/* PDF canvas area */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: 20, width: '100%' }}>
        {loading && (
          <div style={{ color: '#fff', fontSize: 14, alignSelf: 'center' }}>Loading PDF...</div>
        )}
        {error && (
          <div style={{ color: '#F87171', fontSize: 14, alignSelf: 'center', textAlign: 'center' }}>
            <div style={{ marginBottom: 8 }}>Failed to load PDF</div>
            <div style={{ fontSize: 12, color: '#9CA3AF' }}>{error}</div>
          </div>
        )}
        {!loading && !error && (
          <canvas
            ref={canvasRef}
            style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.4)', borderRadius: 2, maxWidth: '100%' }}
          />
        )}
      </div>
    </div>
  );
}
